import { gql, useQuery } from '@apollo/client';
import { AccountStates } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { getAccountName } from '@interfaces/account';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { usePermission } from '../../../hooks/usePermission';

type SelectItem = { key: string; label: string };

type FetchAccountResponse = {
  fetchAccounts: {
    accounts: Pick<Account, 'id' | 'type' | 'custodianAccountNumber'>[];
  };
};

export const FETCH_ACCOUNTS = ({
  canReadAccountNumber,
}: {
  canReadAccountNumber: boolean;
}) => gql`
  query fetchAccounts($filter: AccountsQueryFilter!) {
    fetchAccounts(input: { filter: $filter, pagination: { perPage: 1000 } }) {
      accounts {
        id: stringId
        type
        ${canReadAccountNumber ? 'custodianAccountNumber' : ''}
      }
    }
  }
`;

export const ACCOUNT_STATES = [
  AccountStates.Active,
  AccountStates.Requested,
  AccountStates.Frozen,
  AccountStates.Failed,
  AccountStates.Initiated,
  AccountStates.Ready,
];

export const MultiAccountSelect = ({
  forId,
  label = '',
  error,
  errorText,
  onChange,
  selectedAccountIds,
  onBlur,
  testId,
  states,
}: {
  forId: string;
  label?: string;
  error?: boolean;
  errorText?: string;
  onChange: (event: any) => void;
  selectedAccountIds?: string[];
  onBlur?: () => void;
  testId?: string;
  states?: AccountStates[];
}) => {
  const canReadAccountNumber = usePermission('read:account_number');

  const { data, loading } = useQuery(FETCH_ACCOUNTS({ canReadAccountNumber }), {
    variables: { filter: { userId: forId, states: states || ACCOUNT_STATES } },
    fetchPolicy: 'no-cache',
  });

  const getAccountNames = (accountIds: any): SelectItem[] =>
    accountIds
      .map((account: any) => {
        const fetchedAccount = data?.fetchAccounts?.accounts?.find(
          (fetched: any) => fetched?.id === (account.id || account),
        );
        return {
          key: (fetchedAccount?.id as string) || '',
          label: getAccountName(
            fetchedAccount,
            !!fetchedAccount?.custodianAccountNumber,
          ),
        };
      })
      .filter((obj: SelectItem) => Object.keys(obj).length !== 0);

  return (
    <Autocomplete
      testId={testId ?? 'multi-account-select'}
      multiple
      fullWidth
      label={label}
      disabled={loading}
      onChange={(_e: React.SyntheticEvent, value: SelectItem[]) =>
        onChange(value.map((x) => x.key))
      }
      value={getAccountNames(selectedAccountIds || [])}
      options={
        data?.fetchAccounts?.accounts?.map((x: any) => ({
          key: x.id,
          label: getAccountName(x, !!x.custodianAccountNumber),
        })) || []
      }
      isOptionEqualToValue={(option: SelectItem, value: SelectItem) =>
        option.key === value.key
      }
      renderOption={(props: any, option: SelectItem) => (
        <li {...props}>{option.label}</li>
      )}
      disableCloseOnSelect
      error={error}
      errorText={errorText}
      onBlur={onBlur}
    />
  );
};

export const AccountSelect = ({
  forId,
  onChange,
  includeEmpty,
  value,
  label,
  size,
  testId,
  states,
  error,
  onBlur,
  disabled,
}: {
  forId: string;
  onChange: (event: any) => void;
  onBlur: () => void;
  value: string;
  label: string;
  includeEmpty?: boolean;
  size?: 'small' | 'medium';
  testId?: string;
  states?: AccountStates[];
  error?: boolean;
  disabled?: boolean;
}) => {
  const canReadAccountNumber = usePermission('read:account_number');

  const { data, loading } = useQuery<FetchAccountResponse>(
    FETCH_ACCOUNTS({ canReadAccountNumber }),
    {
      variables: {
        filter: { userId: forId, states: states || ACCOUNT_STATES },
      },
      fetchPolicy: 'no-cache',
    },
  );

  return (
    <SelectField
      value={data ? value : ''}
      label={label}
      fullWidth
      onChange={onChange}
      onBlur={onBlur}
      size={size}
      testId={testId ?? 'account-select'}
      error={error}
      disabled={disabled}
    >
      {includeEmpty && (
        <MenuItem key='empty' value=''>
          -
        </MenuItem>
      )}
      {loading ? (
        <MenuItem>...</MenuItem>
      ) : (
        data?.fetchAccounts?.accounts?.map((account) => (
          <MenuItem
            data-testId={
              testId
                ? `${testId}-${account.type}`
                : `account-select-item-${account.type}`
            }
            key={account.id}
            value={account.id}
          >
            {getAccountName(account, !!account.custodianAccountNumber)}
          </MenuItem>
        ))
      )}
    </SelectField>
  );
};
